import { LogoutOutlined } from "@ant-design/icons";
import { Menu } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { ROLE } from "../constant";
import { logout } from "./../app.slice";
import { useTranslation } from "react-i18next";
import { useFetchChecklistSettingsQuery } from "../pages/settings/settings.service"; 
import { useEffect, useMemo } from "react";

function Sidenav({ color = "#555555" }) {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const userData = useSelector((apps) => apps.app.user);
  const { pathname } = useLocation();
  const page = pathname.split("/")[1];
  const navigate = useNavigate();
  
  // ✅ Add loading and error states for debugging
  const { 
    data: checklistSettingsResponse, 
    refetch,
    isLoading,
    isError 
  } = useFetchChecklistSettingsQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  
  // ✅ Better data extraction
  const checklistSettings = checklistSettingsResponse?.data || checklistSettingsResponse || {};
  
  // ✅ Debug logs
  // console.log("🔄 Sidenav Rendered");
  // console.log("📦 API Response:", checklistSettingsResponse);
  // console.log("🎯 Monthly Value:", checklistSettings?.monthly);
  // console.log("📊 Type of monthly:", typeof checklistSettings?.monthly);
  // console.log("🔍 Boolean check:", checklistSettings?.monthly === true);

  // ✅ Force refetch on mount
  useEffect(() => {
    console.log("🔄 Refetching on mount");
    refetch();
  }, []);

  // ✅ Settings change par refetch
  useEffect(() => {
    const handleSettingsChange = (event) => {
      console.log("📢 Settings updated event received", event.detail);
      refetch();
    };
    
    window.addEventListener('settings-updated', handleSettingsChange);
    
    return () => {
      window.removeEventListener('settings-updated', handleSettingsChange);
    };
  }, []);

  // ✅ IMPORTANT: Dependencies array add karo!
  const menuItems = useMemo(() => {
    // console.log("🏗️ Building menuItems with monthly =", checklistSettings?.monthly);
    
    return [
      {
        label: (
          <NavLink to="/home" style={{ backgroundColor: "rgb(47, 46, 112) " }}>
            <div
              style={{
                color: page === "home" ? "yellow" : "white",
                width: "100%",
              }}
              className="label"
            >
              {t("HOME")}
            </div>
          </NavLink>
        ),
        key: "dash",
        role: [ROLE.RESTAURANT_ADMIN, ROLE.RESTAURANT_USER],
      },
      {
        label: (
          <NavLink
            to="/restaurant"
            style={{ backgroundColor: "rgb(47, 46, 112) " }}
          >
            <div
              style={{
                color: page === "restaurant" ? "yellow" : "white",
                width: "100%",
              }}
              className="label"
            >
              {t("RESTAURANT")}
            </div>
          </NavLink>
        ),
        key: "restaurant",
        role: [ROLE.SUPER_ADMIN],
      },
      {
        label: (
          <NavLink
            to="/compliance"
            style={{ backgroundColor: "rgb(47, 46, 112) " }}
          >
            <div
              style={{
                color: page === "compliance" ? "yellow" : "white",
                width: "100%",
              }}
              className="label"
            >
              {t("COMPLIANCE")}
            </div>
          </NavLink>
        ),
        key: "compliance",
        role: [ROLE.SUPER_ADMIN],
      },

      {
        label: (
          <NavLink
            to="/openchecks"
            style={{ backgroundColor: "rgb(47, 46, 112) " }}
          >
            <div
              className="label"
              style={{
                width: "100%",
                color: page === "openchecks" ? "yellow" : "white",
              }}
            >
              {t("OPENING CHECKS")}
            </div>
          </NavLink>
        ),
        role: [ROLE.RESTAURANT_ADMIN, ROLE.RESTAURANT_USER],
        key: "openchecks",
      },
      {
        label: (
          <NavLink
            to="/provechecks"
            style={{ backgroundColor: "rgb(47, 46, 112)" }}
          >
            <div
              className="label"
              style={{
                width: "100%",
                color: page === "provechecks" ? "yellow" : "white",
              }}
            >
              {t("PROBE CHECKS")}
            </div>
          </NavLink>
        ),
        role: [ROLE.RESTAURANT_ADMIN, ROLE.RESTAURANT_USER],
        key: "provechecks",
      },
      {
        label: (
          <NavLink
            to="/closechecks"
            style={{ backgroundColor: "rgb(47, 46, 112) " }}
          >
            <div
              className="label"
              style={{
                width: "100%",
                color: page === "closechecks" ? "yellow" : "white",
              }}
            >
              {t("CLOSING CHECKS")}
            </div>
          </NavLink>
        ),
        role: [ROLE.RESTAURANT_ADMIN, ROLE.RESTAURANT_USER],
        key: "closechecks",
      },
      {
        label: (
          <NavLink
            to="/weeklychecks"
            style={{ backgroundColor: "rgb(47, 46, 112)" }}
          >
            <div
              className="label"
              style={{
                width: "100%",
                color: page === "weeklychecks" ? "yellow" : "white",
              }}
            >
              {t("WEEKLY CHECKS")}
            </div>
          </NavLink>
        ),
        role: [ROLE.RESTAURANT_ADMIN, ROLE.RESTAURANT_USER],
        key: "weeklychecks",
      },
      // ✅ Monthly menu item with condition
      ...(checklistSettings?.monthly === true ? [{
        label: (
          <NavLink
            to="/monthlychecks"
            style={{ backgroundColor: "rgb(47, 46, 112)" }}
          >
            <div
              className="label"
              style={{
                width: "100%",
                color: page === "monthlychecks" ? "yellow" : "white",
              }}
            >
              {t("MONTHLY CHECKS")}
            </div>
          </NavLink>
        ),
        role: [ROLE.RESTAURANT_ADMIN, ROLE.RESTAURANT_USER],
        key: "monthlychecks",
      }] : []),
      {
        label: (
          <NavLink
            to="/temperatures"
            style={{ backgroundColor: "rgb(47, 46, 112) " }}
          >
            <div
              className="label"
              style={{
                width: "100%",
                color: page === "temperatures" ? "yellow" : "white",
              }}
            >
              {t("TEMPERATURES")}
            </div>
          </NavLink>
        ),
        role: [ROLE.RESTAURANT_ADMIN, ROLE.RESTAURANT_USER],
        key: "temperatures",
      },
      {
        label: (
          <NavLink to="/delivery" style={{ backgroundColor: "rgb(47, 46, 112)" }}>
            <div
              className="label"
              style={{
                width: "100%",
                color: page === "delivery" ? "yellow" : "white",
              }}
            >
              {t("DELIVERY")}
            </div>
          </NavLink>
        ),
        role: [ROLE.RESTAURANT_ADMIN, ROLE.RESTAURANT_USER],
        key: "delivery",
      },
      {
        label: (
          <NavLink to="/supplier" style={{ backgroundColor: "rgb(47, 46, 112)" }}>
            <div
              className="label"
              style={{
                width: "100%",
                color: page === "supplier" ? "yellow" : "white",
              }}
            >
              {t("SUPPLIER")}
            </div>
          </NavLink>
        ),
        role: [ROLE.RESTAURANT_ADMIN, ROLE.RESTAURANT_USER],
        key: "supplier",
      },
      {
        label: (
          <NavLink to="/team" style={{ backgroundColor: "rgb(47, 46, 112) " }}>
            <div
              className="label"
              style={{
                width: "100%",
                color: page === "team" ? "yellow" : "white",
              }}
            >
              {t("TEAM")}
            </div>
          </NavLink>
        ),
        role: [ROLE.RESTAURANT_ADMIN, ROLE.RESTAURANT_USER],
        key: "team",
      },
      {
        label: (
          <NavLink
            to="/incidents"
            style={{ backgroundColor: "rgb(47, 46, 112) " }}
          >
            <div
              className="label"
              style={{
                width: "100%",
                color: page === "incidents" ? "yellow" : "white",
              }}
            >
              {t("INCIDENTS")}
            </div>
          </NavLink>
        ),
        role: [ROLE.RESTAURANT_ADMIN, ROLE.RESTAURANT_USER],
        key: "incidents",
      },

      
  {
  key: "label",
  label: (
    <div
      className="label"
      style={{
        width: "100%",
        color: page === "label" ? "yellow" : "white",
      }}
    >
      {t("LABEL GENERATOR")}
    </div>
  ),
  role: [ROLE.RESTAURANT_ADMIN],
  children: [
    {
      key: "label-category",
      label: (
        <NavLink to="/label/category">
          <span style={{ color: "white" }}>{t("CATEGORIES")}</span>
        </NavLink>
      ),
    },
    {
      key: "allergens",
      label: (
        <NavLink to="/label/allergens">
          <span style={{ color: "white" }}>{t("ALLERGENS")}</span>
        </NavLink>
      ),
    },
  ],
},
      {
        label: (
          <NavLink to="/reports" style={{ backgroundColor: "rgb(47, 46, 112) " }}>
            <div
              className="label"
              style={{
                width: "100%",
                color: page === "reports" ? "yellow" : "white",
              }}
            >
              {t("REPORTS")}
            </div>
          </NavLink>
        ),
        role: [ROLE.RESTAURANT_ADMIN, ROLE.RESTAURANT_USER],
        key: "reports",
      },
      {
        label: (
          <NavLink to="/allergy" style={{ backgroundColor: "rgb(47, 46, 112) " }}>
            <div
              className="label"
              style={{
                width: "100%",
                color: page === "allergy" ? "yellow" : "white",
              }}
            >
              {t("ALLERGY CHECK")}
            </div>
          </NavLink>
        ),
        role: [ROLE.RESTAURANT_ADMIN, ROLE.RESTAURANT_USER],
        key: "allergy",
      },
      {
        label: (
          <NavLink
            to="/compliance_checks"
            style={{ backgroundColor: "rgb(47, 46, 112) " }}
          >
            <div
              className="label"
              style={{
                width: "100%",
                color: page === "compliance_checks" ? "yellow" : "white",
              }}
            >
              {t("COMPLIANCE")}
            </div>
          </NavLink>
        ),
        role: [ROLE.RESTAURANT_ADMIN, ROLE.RESTAURANT_USER],
        key: "compliance_checks",
      },
      {
        label: (
          <NavLink
            to="/settings"
            style={{ backgroundColor: "rgb(47, 46, 112) " }}
          >
            <div
              className="label"
              style={{
                width: "100%",
                color: page === "settings" ? "yellow" : "white",
              }}
            >
              {t("SETTINGS")}
            </div>
          </NavLink>
        ),
        role: [ROLE.RESTAURANT_ADMIN, ROLE.RESTAURANT_USER],
        key: "settings",
      },

    ];
  }, [page, t, checklistSettings?.monthly]); // ✅ YEH DEPENDENCIES IMPORTANT HAIN!

  const filteredMenuItems = menuItems.filter(
    (item) => item.role.includes(userData.role)
  );

  // ✅ Agar loading hai to simple menu dikhao
  if (isLoading) {
    return (
      <div style={{ padding: "20px", color: "white" }}>
        Loading menu...
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "95vh",
      }}
    >
      <div
        className="brand"
        style={{
          textAlign: "center",
          marginTop: "10px",
          color: "white",
          fontSize: "20px",
          marginBottom: "20px",
        }}
      >
        <img
          style={{ width: "170px" }}
          decoding="async"
          src={"./mark.jpg"}
          alt="logo"
        />
      </div>
      <div style={{ flex: 1 }}>
        <Menu theme="light" mode="inline" items={filteredMenuItems}></Menu>
      </div>
      <div style={{ height: "50px" }}>
        <div
          className="logoutlabel"
          style={{
            width: "100%",
            color: "white",
            cursor: "pointer",
          }}
          onClick={() => {
            dispatch(logout());
            navigate("/login");
            window.location.reload();
          }}
        >
          <LogoutOutlined /> {t("LOG OUT")}
        </div>
      </div>
    </div>
  );
}

export default Sidenav;